import './HomeOrderCard.scss';

import classNames from 'classnames';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DefaultModal from '@components/DefaultModal/defaultModal';
import { CURES_ACT_LANGUAGE, CURES_ACT_MODAL_BUTTONS_INFO } from '@constants/constants';
import { ROUTES } from '@constants/routes';
import { OrderStatus } from '@customTypes/order';
import type { Requisition } from '@customTypes/requisition';
import { useCollatedReportDownload } from '@hooks/useCollatedReportDownload';
import IconChevronDown from '@pages/order/components/common/IconChevronDown';
import { getProductTestDisplayName, stripMyOmePrefix } from '@pages/order/utils/productDisplayName';
import ArrowRightSvg from '@statics/images/arrow-right.svg?react';
import { makeCuresActRelease } from '@store/actions';
import { formatDate, recordGoogleAnalyticsEvent } from '@utils/helpers';

import { HOME_STAGE_DETAIL, HOME_STAGE_DETAIL_COPY, type HomeStageDetail } from '../homeStageDetail';
import { buildHomeOrderTimeline, getHomeOrderProgress, type HomeTerminalStatus } from '../homeTimeline';
import { HomeOrderTimeline as HomeOrderTimelineRail } from './HomeOrderTimeline';

const terminalPanelHeading = (status: HomeTerminalStatus): string => {
  switch (status) {
    case OrderStatus.CANCELED:
      return 'This order was canceled';
    case OrderStatus.DENIED:
      return 'This order was denied';
    case OrderStatus.FAILED:
      return 'We could not process your sample';
  }
};

function ResourceAction({
  actionLabel,
  href,
  requisitionId,
}: {
  actionLabel?: string;
  href?: string;
  requisitionId: string;
}) {
  if (!actionLabel || !href) return null;

  if (href.startsWith('/')) {
    return (
      <Link className='home-order-card__resource-action' to={href.replace(':reqId', requisitionId)}>
        {actionLabel}
      </Link>
    );
  }

  return (
    <a className='home-order-card__resource-action' href={href} target='_blank' rel='noreferrer'>
      {actionLabel}
    </a>
  );
}

export const getProductNames = (requisition: Requisition): string[] =>
  (requisition.orders ?? [])
    .map(order => order.product_name)
    .filter(Boolean)
    .map(stripMyOmePrefix);

const VISIBLE_LOGO_COUNT = 3;

export type ProductLogoGroup = {
  logoUrl?: string;
  /** product_family_name. Parent line, image alt, and the +N more label. */
  familyName?: string;
  names: string[];
};

/** Tests that share a logo_url, first-seen order. No-logo orders group on family name. */
export const getProductLogoGroups = (requisition: Requisition): ProductLogoGroup[] => {
  const groups: ProductLogoGroup[] = [];
  const byLogo = new Map<string, ProductLogoGroup>();
  const byFamily = new Map<string, ProductLogoGroup>();

  for (const order of requisition.orders ?? []) {
    const name = getProductTestDisplayName({
      name: order.product_name,
      product_test_name: order.product_test_name,
    });
    if (!name) continue;

    const existing = order.logo_url
      ? byLogo.get(order.logo_url)
      : order.product_family_name
        ? byFamily.get(order.product_family_name)
        : undefined;
    if (existing) {
      if (!existing.names.includes(name)) existing.names.push(name);
      if (!existing.familyName && order.product_family_name) existing.familyName = order.product_family_name;
      continue;
    }

    const group: ProductLogoGroup = { names: [name] };
    if (order.logo_url) {
      group.logoUrl = order.logo_url;
      byLogo.set(order.logo_url, group);
    } else if (order.product_family_name) {
      byFamily.set(order.product_family_name, group);
    }
    if (order.product_family_name) group.familyName = order.product_family_name;
    groups.push(group);
  }

  return groups;
};

/** Indented tests when a group has 2+ tests, or its one test name differs from the family name. */
export const showsChildTests = (group: ProductLogoGroup): boolean =>
  group.names.length >= 2 || Boolean(group.familyName && group.names[0] !== group.familyName);

export interface HomeOrderCardProps {
  requisition: Requisition;
  /** Defaults to expanded so the current order opens with its full timeline. */
  defaultExpanded?: boolean;
  /** Confirmed against the requisition detail payload, not inferred from order status. */
  canDownload?: boolean;
  /** My Results, including the selected dependent when Home is not showing self. */
  resultsTo?: string;
  /** Called after a Cures Act early release succeeds so the parent can reload the order. */
  onCuresActReleased?: () => void;
}

export const HomeOrderCard = (props: HomeOrderCardProps) => {
  const {
    requisition,
    defaultExpanded = true,
    canDownload = false,
    resultsTo = ROUTES.RESULTS,
    onCuresActReleased,
  } = props;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showCuresActModal, setShowCuresActModal] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const bodyId = useId();

  // Same modal and release call the order details page uses; only the entry point is new.
  const onCuresActModalBtnClick = async (btnType: string) => {
    if (btnType === 'WAIT_FOR_RESULTS') {
      recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Wait for Results');
      setShowCuresActModal(false);
      return;
    }
    if (btnType !== 'REQUEST_WITHOUT_COUNSELING') return;

    recordGoogleAnalyticsEvent('User Interaction', 'Button Click', 'Request Early Release');
    setShowCuresActModal(false);
    setIsReleasing(true);
    try {
      await makeCuresActRelease({ requisition_id: requisition.requisition_id });
      onCuresActReleased?.();
    } catch (error) {
      console.error('Error requesting Cures Act release from Home:', error);
    } finally {
      setIsReleasing(false);
    }
  };
  const { isDownloading, onDownload } = useCollatedReportDownload(requisition.requisition_id);
  const timeline = buildHomeOrderTimeline(requisition);
  const progress = getHomeOrderProgress(requisition);
  const productGroups = getProductLogoGroups(requisition);
  const logoGroups = productGroups.filter(group => group.logoUrl);
  const visibleLogos = logoGroups.slice(0, VISIBLE_LOGO_COUNT);
  const hiddenLogos = logoGroups.slice(VISIBLE_LOGO_COUNT);
  const hiddenLogoLabel = hiddenLogos
    .map(group => group.familyName || group.names[0])
    .filter(Boolean)
    .join(', ');
  const currentStep = timeline?.kind === 'progress' ? timeline.steps.find(step => step.status === 'current') : null;
  const statusLabel = timeline?.kind === 'terminal' ? timeline.label : currentStep?.label;
  const isResultsReady = timeline?.kind === 'progress' && timeline.currentStage === 'results-ready';

  const showResultsLink = progress.reported > 0;
  const isTerminal = timeline?.kind === 'terminal';
  const hasPrimaryAction = showResultsLink || canDownload;
  const showResultsStrip = !isExpanded && hasPrimaryAction;
  const resultsStripHeading = isResultsReady
    ? HOME_STAGE_DETAIL['results-ready'].heading
    : progress.isPartiallyReported
      ? `${progress.reported} of ${progress.total} reports ready`
      : null;

  const downloadButton = canDownload ? (
    <button
      type='button'
      className='home-order-card__download'
      onClick={onDownload}
      disabled={isDownloading}
      data-testid='homeOrderCardDownload'
    >
      <FontAwesomeIcon icon={faArrowDown} aria-hidden='true' />
      Download Reports
    </button>
  ) : null;

  const resultsActions = hasPrimaryAction ? (
    <>
      {showResultsLink && (
        <Link className='home-cta' to={resultsTo}>
          <span>View results</span>
          <ArrowRightSvg aria-hidden='true' />
        </Link>
      )}
      {downloadButton}
    </>
  ) : null;

  // Terminal orders have no stage detail, so they keep the plain summary heading.
  const detail: HomeStageDetail | null =
    timeline?.kind === 'progress' ? HOME_STAGE_DETAIL[timeline.currentStage] : null;
  // Terminal orders explain themselves in the stage panel; the status badge above
  // already names the state, so a separate terminal block would just repeat it.
  const panelHeading = timeline?.kind === 'terminal' ? terminalPanelHeading(timeline.status) : detail?.heading;
  // The whole stage panel collapses with the rest of the detail; only the summary survives.
  const showStagePanel = isExpanded && (Boolean(panelHeading) || progress.isPartiallyReported || hasPrimaryAction);

  return (
    <article
      className={classNames('home-order-card', !isExpanded && 'home-order-card--collapsed')}
      data-testid='homeOrderCard'
    >
      <div className='home-order-card__summary'>
        <div className='home-order-card__facts'>
          <div className='home-order-card__header-main'>
            <dl className='home-order-card__fact-list'>
              <div className='home-order-card__fact'>
                <dt>Order ID</dt>
                <dd data-testid='homeOrderCardReqId'>{requisition.requisition_id}</dd>
              </div>
              {requisition.submitted_at && (
                <div className='home-order-card__fact'>
                  <dt>Order date</dt>
                  <dd>{formatDate(requisition.submitted_at)}</dd>
                </div>
              )}
              {statusLabel && (
                <div className='home-order-card__fact home-order-card__fact--status'>
                  <dt>Status</dt>
                  <dd data-testid='homeOrderCardStatus'>
                    <span className='home-order-card__status-dot' aria-hidden='true' />
                    {statusLabel}
                  </dd>
                </div>
              )}
            </dl>
            {visibleLogos.length > 0 && (
              <div className='home-order-card__logo-stack' data-testid='homeOrderCardLogos'>
                {visibleLogos.map((group, index) => (
                  <span
                    key={group.logoUrl}
                    className='home-order-card__logo'
                    style={{ zIndex: VISIBLE_LOGO_COUNT - index }}
                  >
                    <img src={group.logoUrl} alt={group.familyName || group.names[0] || ''} loading='lazy' />
                  </span>
                ))}
                {hiddenLogos.length > 0 && (
                  <span className='home-order-card__logo-more' title={hiddenLogoLabel} data-testid='homeOrderCardLogoMore'>
                    {`+${hiddenLogos.length} more`}
                    {hiddenLogoLabel && <span className='visually-hidden'>{hiddenLogoLabel}</span>}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className='home-order-card__fact-actions'>
            <button
              type='button'
              className={classNames('home-order-card__toggle', isExpanded && 'is-open')}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
              aria-label={isExpanded ? 'Hide order details' : 'Show order details'}
              onClick={() => setIsExpanded(expanded => !expanded)}
              data-testid='homeOrderCardToggle'
            >
              <IconChevronDown className='home-order-card__chevron' />
            </button>
          </div>
        </div>

        {isExpanded && productGroups.length > 0 && (
          <div className='home-order-card__tests' data-testid='homeOrderCardTests'>
            <p className='home-order-card__tests-label'>Tests</p>
            <ul className='home-order-card__test-groups'>
              {productGroups.map((group, index) => (
                <li key={group.logoUrl ?? `names-${index}`} className='home-order-card__test-group'>
                  <p className='home-order-card__test-row'>
                    <span className='home-order-card__test-dot' aria-hidden='true' />
                    <span>{group.familyName || group.names[0]}</span>
                  </p>
                  {showsChildTests(group) && (
                    <ul className='home-order-card__test-children'>
                      {(group.familyName ? group.names : group.names.slice(1)).map(name => (
                        <li key={name} className='home-order-card__test-row'>
                          <span
                            className='home-order-card__test-dot home-order-card__test-dot--hollow'
                            aria-hidden='true'
                          />
                          <span>{name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {showResultsStrip && (
        <div className='home-order-card__results-strip' data-testid='homeOrderCardResultsStrip'>
          {resultsStripHeading && <p className='home-order-card__results-heading'>{resultsStripHeading}</p>}
          <div className='home-order-card__actions'>{resultsActions}</div>
        </div>
      )}

      <div id={bodyId}>
        {isExpanded && timeline?.kind === 'progress' && (
          <div className='home-order-card__timeline'>
            <HomeOrderTimelineRail timeline={timeline} />
          </div>
        )}

        {/* Nested rather than a sibling block, so the action always reads as belonging to this order. */}
        {showStagePanel && (
          <div className='home-order-card__stage-panel' data-testid='homeOrderStagePanel'>
            <div
              className={classNames(
                'home-order-card__stage-main',
                (detail?.video || detail?.image) && 'home-order-card__stage-main--with-video',
              )}
            >
              <div className='home-order-card__stage-copy'>
                {panelHeading && <h3 className='home-order-card__stage-heading'>{panelHeading}</h3>}
                {detail?.body.map(paragraph => (
                  <p key={paragraph} className='home-order-card__stage-body'>
                    {paragraph}
                  </p>
                ))}
                {detail?.curesRelease && (
                  <p className='home-order-card__stage-body'>
                    {detail.curesRelease.lead}{' '}
                    <button
                      type='button'
                      className='home-order-card__inline-action'
                      onClick={() => setShowCuresActModal(true)}
                      disabled={isReleasing}
                      data-testid='homeOrderCuresRequest'
                    >
                      {detail.curesRelease.actionLabel}
                    </button>
                  </p>
                )}
                {progress.isPartiallyReported && (
                  <p className='home-order-card__stage-detail'>
                    {`${progress.reported} of ${progress.total} reports ready`}
                  </p>
                )}

                {detail?.notes?.length ? (
                  <div className='home-order-card__notes'>
                    {detail.noteHeading && <h4 className='home-order-card__note-heading'>{detail.noteHeading}</h4>}
                    <ul className='home-order-card__note-list'>
                      {detail.notes.map(note => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {detail?.closing && <p className='home-order-card__stage-body'>{detail.closing}</p>}

                {isTerminal && (
                  <p className='home-order-card__stage-body'>Contact MyOme support if you have other questions.</p>
                )}

                {detail?.upNext && (
                  <div className='home-order-card__up-next' data-testid='homeOrderUpNext'>
                    <p className='home-order-card__up-next-label'>{HOME_STAGE_DETAIL_COPY.upNextLabel}</p>
                    <p className='home-order-card__up-next-text'>{detail.upNext}</p>
                  </div>
                )}

                {hasPrimaryAction && <div className='home-order-card__actions'>{resultsActions}</div>}
              </div>

              {detail?.video?.embedSrc && (
                <div className='home-order-card__video home-order-card__video--embed' data-testid='homeOrderVideo'>
                  <iframe
                    src={detail.video.embedSrc}
                    title={detail.video.title}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                    allowFullScreen
                  />
                </div>
              )}
              {!detail?.video && detail?.image && (
                <div className='home-order-card__video home-order-card__video--image' data-testid='homeOrderImage'>
                  <img src={detail.image.src} alt={detail.image.alt} />
                </div>
              )}
            </div>
          </div>
        )}

        {isExpanded && detail?.resources?.length ? (
          <div className='home-order-card__resources' data-testid='homeOrderResources'>
            <h4 className='home-order-card__resources-title'>{HOME_STAGE_DETAIL_COPY.resourcesTitle}</h4>
            <ul className='home-order-card__resource-list'>
              {detail.resources.map(resource => (
                <li className='home-order-card__resource' key={resource.id}>
                  <span className='home-order-card__resource-icon'>
                    <img src={resource.icon} alt='' />
                  </span>
                  <h5 className='home-order-card__resource-title'>{resource.title}</h5>
                  <p className='home-order-card__resource-body'>{resource.body}</p>
                  <ResourceAction
                    actionLabel={resource.actionLabel}
                    href={resource.href}
                    requisitionId={requisition.requisition_id}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {detail?.curesRelease && (
        <DefaultModal
          show={showCuresActModal}
          modalTitle='Request Access to Report Without Clinician Review'
          modalButtons={CURES_ACT_MODAL_BUTTONS_INFO}
          toggleModal={setShowCuresActModal}
          onModalBtnClick={onCuresActModalBtnClick}
        >
          <p>{CURES_ACT_LANGUAGE.PART_ONE}</p>
          <p>{CURES_ACT_LANGUAGE.PART_TWO}</p>
        </DefaultModal>
      )}
    </article>
  );
};
